import React from 'react';
import type { OrderIssueType, OrderIssueSource, OrderIssueStatus } from '../../types/orderIssues';

const ISSUE_TYPE_CONFIG: Record<OrderIssueType, { label: string; color: string }> = {
  label_generation_failed: {
    label: 'Label Failed',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  address_validation_error: {
    label: 'Address Error',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  postnl_api_error: {
    label: 'PostNL Error',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  shopify_fulfillment_error: {
    label: 'Shopify Error',
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  incomplete_address: {
    label: 'Incomplete Address',
    color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  },
  postal_code_invalid: {
    label: 'Postal Code',
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  other: {
    label: 'Other',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  },
};

const SOURCE_CONFIG: Record<OrderIssueSource, { label: string; color: string }> = {
  shipping_labels_manual: {
    label: 'Manual Label',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  worker_terminal: {
    label: 'Worker Terminal',
    color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  },
  finish_order_modal: {
    label: 'Finish Order',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  bulk_finish_orders: {
    label: 'Bulk Finish',
    color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  },
  postnl_label_modal: {
    label: 'PostNL Modal',
    color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  },
  ai_address_validation: {
    label: 'AI Address',
    color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  },
  unknown: {
    label: 'Unknown',
    color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  },
};

const STATUS_CONFIG: Record<OrderIssueStatus, { label: string; color: string }> = {
  open: {
    label: 'Open',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  under_process: {
    label: 'Under Process',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  processed: {
    label: 'Processed',
    color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  },
  resolved: {
    label: 'Resolved',
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  },
  ignored: {
    label: 'Ignored',
    color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  },
};

export const IssueTypeBadge: React.FC<{ type: OrderIssueType }> = ({ type }) => {
  const config = ISSUE_TYPE_CONFIG[type] || ISSUE_TYPE_CONFIG.other;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

export const SourceBadge: React.FC<{ source: OrderIssueSource }> = ({ source }) => {
  const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.unknown;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

export const StatusBadge: React.FC<{ status: OrderIssueStatus }> = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};
